import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, Tab } from '@mui/material';
import { apiService } from '../api/apiClient';

import AuditConfigBanner from './traceability/AuditConfigBanner';
import TraceDetailDrawer from './traceability/TraceDetailDrawer';

import ChatInteractionsTab from './traceability/tabs/ChatInteractionsTab';
import LLMCallsTab from './traceability/tabs/LLMCallsTab';
import CypherExecutionsTab from './traceability/tabs/CypherExecutionsTab';
import IngestionTab from './traceability/tabs/IngestionTab';
import MutationsTab from './traceability/tabs/MutationsTab';
import SparqlTab from './traceability/tabs/SparqlTab';
import KGToTextTab from './traceability/tabs/KGToTextTab';
import TracesTab from './traceability/tabs/TracesTab';
import AnalyticsTab from './traceability/tabs/AnalyticsTab';
import AIActTab from './traceability/tabs/AIActTab';
import ProvenanceTab from './traceability/tabs/ProvenanceTab';

const TAB_KEYS = [
  'chat', 'llm', 'cypher', 'ingestion', 'mutations', 'provenance',
  'sparql', 'kgToText', 'traces', 'analytics', 'aiAct',
];

export default function Traceability({ prefill }) {
  const { t } = useTranslation();
  const [tabIdx, setTabIdx] = useState(0);
  const [config, setConfig] = useState(null);
  const [activeTraceId, setActiveTraceId] = useState(null);

  useEffect(() => {
    apiService.getAuditConfig()
      .then(setConfig)
      .catch(() => setConfig({ writes_enabled: false, read_configured: false }));
  }, []);

  // When a prefill lands (e.g., user clicked "Ver razonamiento" in the chat),
  // jump to the Sessions tab and auto-open the drawer for that trace.
  useEffect(() => {
    if (prefill?.traceId) {
      setTabIdx(TAB_KEYS.indexOf('traces'));
      setActiveTraceId(prefill.traceId);
    }
  }, [prefill?.traceId, prefill?.at]);

  const goToTab = (key) => {
    const i = TAB_KEYS.indexOf(key);
    if (i >= 0) setTabIdx(i);
  };

  return (
    <div className="h-full flex flex-col p-8 overflow-hidden">
      <header className="mb-4 shrink-0">
        <p className="text-label text-ink-muted">{t('content:traceabilitySectionIntro')}</p>
      </header>

      <AuditConfigBanner config={config} />

      <Tabs
        value={tabIdx}
        onChange={(_, v) => setTabIdx(v)}
        variant="scrollable"
        scrollButtons="auto"
        className="shrink-0 border-b border-border-subtle"
        sx={{
          minHeight: 40,
          '& .MuiTab-root': {
            color: '#a8a29e', fontSize: 12, textTransform: 'uppercase',
            letterSpacing: '0.1em', minHeight: 40, padding: '6px 14px',
          },
          '& .Mui-selected': { color: '#d4a574 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#d4a574' },
        }}
      >
        {TAB_KEYS.map((k) => (
          <Tab key={k} label={t(`traceability:tabs.${k}`)} />
        ))}
      </Tabs>

      <div className="flex-1 min-h-0 overflow-auto pt-4">
        {tabIdx === 0 && <ChatInteractionsTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 1 && <LLMCallsTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 2 && <CypherExecutionsTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 3 && <IngestionTab />}
        {tabIdx === 4 && <MutationsTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 5 && <ProvenanceTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 6 && <SparqlTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 7 && <KGToTextTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 8 && <TracesTab onOpenTrace={setActiveTraceId} />}
        {tabIdx === 9 && <AnalyticsTab />}
        {tabIdx === 10 && <AIActTab onGoToTab={goToTab} />}
      </div>

      {activeTraceId && (
        <TraceDetailDrawer traceId={activeTraceId} onClose={() => setActiveTraceId(null)} />
      )}
    </div>
  );
}
